import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import axiosRetry from "axios-retry";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { editPostText } from "../../actions";

axiosRetry(axios, { retries: 3, retryCondition: (_error) => true });

export const copyText = (string) => {
  let textarea;

  try {
    textarea = document.createElement("textarea");
    textarea.setAttribute("readonly", true);
    textarea.setAttribute("contenteditable", true);
    textarea.style.position = "fixed"; // prevent scroll from jumping to the bottom when focus is set.
    textarea.value = string;

    document.body.appendChild(textarea);

    textarea.focus();
    textarea.select();

    const range = document.createRange();
    range.selectNodeContents(textarea);

    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    textarea.setSelectionRange(0, textarea.value.length);
    document.execCommand("copy");
  } catch (err) {
    console.error(err);
  } finally {
    document.body.removeChild(textarea);
  }
};

export const fixMediaSize = (
  img,
  minRatio,
  maxRatio,
  parent = null,
  depth = 0
) => {
  if (img.naturalHeight > 0.0) {
    var ratio = img.naturalWidth / img.naturalHeight;

    //console.log("Original ratio: " + ratio.toFixed(2), img.src);
    if (maxRatio && ratio > maxRatio) {
      ratio = maxRatio;
      //console.log("Changed to: " + ratio.toFixed(2));
    } else if (minRatio && ratio < minRatio) {
      ratio = minRatio;
      //console.log("Changed to: " + ratio.toFixed(2));
    }

    var padding = 100 / ratio;
    var padding_str = padding.toFixed(2) + "%";

    const div = parent ? parent : img.parentNode;
    if (div) {
      div.style.setProperty("--aspect_ratio", padding_str);
    }
  } else if (depth < 50) {
    //console.log("Wait for image to load...");
    setTimeout(() => {
      fixMediaSize(img, minRatio, maxRatio, parent, depth + 1);
    }, 50);
  }
};

export async function downloadFile(media, mediaType) {
  await toast.promise(
    axios
      .get(media, { responseType: "blob" })
      .then((response) => {
        return response.data;
      })
      .then((blob) => {
        var file = new File([blob], "blob", {
          type:
            mediaType === "video"
              ? "video/mp4"
              : mediaType === "gif" || mediaType === "giphy"
              ? "image/gif"
              : media.slice(-4) === ".png"
              ? "image/png"
              : "image/jpeg",
        });
        saveDownloadedFile(file);
      })
      .catch((err) => {
        if (!err instanceof DOMException || err.name !== "AbortError") {
          console.log(err);
          alert("Failed to download file!");
        }
      }),
    {
      pending: "Downloading media...",
      success: "Media download successful",
      error: "Media download failed. Please try again.",
    }
  );
}

export function saveDownloadedFile(file) {
  let tempUrl = URL.createObjectURL(file);
  let aTag = document.createElement("a");
  aTag.href = tempUrl;
  aTag.download = tempUrl.replace(/^.*[\\/]/, "");
  document.body.appendChild(aTag);
  aTag.click();
  URL.revokeObjectURL(tempUrl);
  aTag.remove();
}

export function scrollToTargetAdjusted(target, depth = 0) {
  if (depth < 10) {
    const element = document.getElementById(target);
    if (element) {
      element.scrollIntoView({
        behavior: "auto" /*or smooth*/,
        block: "center",
      });
    } else {
      console.log(target, "not found!");
      setTimeout(() => {
        scrollToTargetAdjusted(target, depth + 1);
      }, 50);
    }
  }
}

export function isTwitterMediaLink(url) {
  if (
    (url.includes("https://twitter.com/") &&
      url.includes("status") &&
      url.includes("video")) ||
    url.includes("https://pic.twitter.com/")
  ) {
    return true;
  } else {
    return false;
  }
}

export const ReadMore = ({ enable, thresh, children, onClick, i18n }) => {
  const [isReadMore, setIsReadMore] = useState(true);
  const toggleReadMore = () => {
    const newVal = !isReadMore;
    setIsReadMore(newVal);
    if(onClick) {
      onClick(!newVal);
    }
  };

  var text = "";
  var displayChildren = [];
  var shortenText = false;
  const tolerance = 40;

  var elements = children;

  if ((typeof elements === "string" || elements instanceof String)) {
    if (elements.length > thresh+tolerance) {
      displayChildren.push(elements.slice(0, thresh).trimEnd());
      shortenText = true;
    }
  } else {
    for (let i = 0; i < elements.length; i++) {
      if (
        typeof elements[i] === "string" ||
        elements[i] instanceof String
      ) {
        if (!shortenText) {
          if (text.length + elements[i].length > thresh+tolerance) {
            displayChildren.push(
              elements[i].slice(0, Math.max(1, thresh - text.length)).trimEnd()
            );
            shortenText = true;
          } else {
            text += children[0][i];
            displayChildren.push(elements[i]);
          }
        }
      } else if (!shortenText) {
        text += "[[enter text]]";
        displayChildren.push(elements[i]);
      } else if (shortenText && isReadMore) {
        if (elements[i]["props"] && elements[i]["props"]["open"]) {
          setIsReadMore(false);
          break;
        }
      }
    }
  }

  return (
    <>
      {enable && isReadMore && shortenText ? displayChildren : children}
      {enable && shortenText && (
        <span onClick={toggleReadMore} className="read-or-hide">
          {isReadMore 
            ? `... ${i18n.getText(i18nKeys.readMore)}`
            : ` ${i18n.getText(i18nKeys.showLess)}`
          }
        </span>
      )}
    </>
  );
};

function htmlDecode(input) {
  var doc = new DOMParser().parseFromString(input.replaceAll("<br>","\n"), "text/html");
  return doc.documentElement.textContent;
}

export const EditableTextContent = ({
  initialText,
  placeholderText,
  platformColor,
  postID,
  insertText,
}) => {
  const textareaRef = useRef(null);
  const [text, setText] = useState(() => {
    let formattedText = initialText;
    const regex = /\[\[(.*?)\]\]/g;
    let matches = [];
    let match = regex.exec(initialText);

    while (match !== null) {
      matches.push(match);
      match = regex.exec(initialText);
    }

    matches.forEach((match, index) => {
      const [fullMatch, innerText] = match;
      const replacement = insertText[index];
      if (typeof replacement !== "undefined" && replacement !== "") {
        formattedText = formattedText.replace(
          fullMatch,
          `<span style="font-style: italic;" eid=${index}>${replacement}</span>`
        );
      } else {
        formattedText = formattedText.replace(
          fullMatch,
          `<span style="font-style: italic;" eid=${index}>${innerText}</span>`
        );
      }
    });

    formattedText = formattedText.replace(/\[\[.*?\]\]/g, "");

    return formattedText;
  });

  const dispatch = useDispatch();

  // Function to adjust the height of the textarea element to fit the content
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Focus on the textarea element and place the cursor at the end of the text
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();

      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(textareaRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);

      adjustTextareaHeight();
    }
  }, []);

  // Close the textarea if clicked outside it and remove span tags
  useEffect(() => {
    const handleClick = (event) => {
      if (textareaRef.current && !textareaRef.current.contains(event.target) && !textareaRef.current.parentNode.contains(event.target)) {
        const innerHTML = textareaRef.current.innerHTML;
        const regex = /<span[^>]*>(.*?)<\/span>/g;
        let updatedText = "";
        const originalTextArray = initialText.match(/\[\[(.*?)\]\]/g);

        updatedText = innerHTML.replace(regex, (match, textAfterChange) => {
          let spanId = null;
          if (match.includes("eid=")) {
            var el = document.createElement("html");
            el.innerHTML = "<html>" + match + "</html>";
            const span = el.getElementsByTagName("span")[0];
            spanId = span.getAttribute("eid");
          }

          let insertedText =
            insertText.length > spanId ? insertText[spanId].trim() : "";
          let placeholderText = "";
          if (
            spanId &&
            originalTextArray &&
            originalTextArray.length > spanId
          ) {
            placeholderText = originalTextArray[spanId].slice(2, -2);
          }

          if (placeholderText === "") {
            return textAfterChange;
          } else if (placeholderText === textAfterChange) {
            return "[[" + placeholderText + "]]";
          } else if (insertedText === textAfterChange) {
            return "[[" + placeholderText + "]]";
          } else {
            return textAfterChange;
          }
        });

        updatedText = htmlDecode(updatedText);

        if ((text.includes("[AI-BEGIN]")) && (text.includes("[AI-END]"))) {
          const parts = text.split("[AI-BEGIN]");
          for(var i=1;i<parts.length;i++) {
            const s = parts[i].split("[AI-END]")[0];
            updatedText = updatedText.replace(s,"[AI-BEGIN]"+s+"[AI-END]");
          }
        }

        setText(updatedText);
        dispatch(editPostText(postID, updatedText));        
      }
    };

    document.addEventListener("mousedown", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [textareaRef, text, postID, dispatch, insertText, initialText]);

  const handleChange = useCallback(
    (event) => {
      event.preventDefault();

      const newText = event.target.innerHTML;
      setText(newText);
      adjustTextareaHeight();
    },
    [textareaRef]
  );

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [text]);

  // Prevents <br> and <div></div> tags from being appended to the end of text when user creates new line
  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      const range = window.getSelection().getRangeAt(0);
      range.deleteContents();
      const newline = document.createTextNode("\n"); // Create a newline character
      range.insertNode(newline);
      range.setEndAfter(newline);
      range.setStartAfter(newline);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      adjustTextareaHeight();
    }
  }, []);

  return (
      <div
        ref={textareaRef}
        contentEditable
        dangerouslySetInnerHTML={{ __html: text.replace(/\[AI-BEGIN\]|\[AI-END\]/gi, "") }}
        placeholder={placeholderText}
        className="editable-textarea"
        style={{
          "--platform_color": platformColor,
          outline: "none",
          border: "none",
          caretColor: platformColor,
          resize: "none",
          width: "100%",
          height: "auto",
          overflow: "hidden",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
        }}
        onBlur={handleChange}
        onPaste={handlePaste}
        onInput={adjustTextareaHeight}
        onKeyDown={handleKeyDown}
      />
  );
};

export const ContentEditableWithRef = (props) => {
  const [defaultValue,setDefaultValue] = useState(props.value);

  const handleInput = (event) => {
    if (props.onChange) {
      props.onChange(event, props.index);
    }
  };

  useEffect(() => {
    if(props.trigger) {
      setDefaultValue(props.value);
    }
  },[props.trigger]);

  const handleDoubleClick = (e) => {
    if(window.getSelection().toString()) {
      let sel = window.getSelection();
      sel.modify("extend","backward","character");
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    var text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  return (
    <span
      style={{
        "--placeholder-text": '"' + props.placeholderText + '"',
        "--platform_color": props.platformColor,
      }}
      contentEditable="true"
      key={props.index}
      className={`fill-in-the-blank`}
      onInput={(e) => handleInput(e)}
      onPaste={(e) => handlePaste(e)}
      onDoubleClick={(e) => handleDoubleClick(e)}
      dangerouslySetInnerHTML={{ __html: defaultValue }}
    ></span>
  );
};

export const addhttp = (url) => {
  if (!/^(?:f|ht)tps?:\/\//.test(url)) {
    url = "http://" + url;
  }
  return url;
};

export const replaceOrAppend = (str, search, replace) => {
  if(str.includes(search)) {
    return str.replace(search,replace);
  } else {
    return str + " " + replace;
  }
};

export const i18nKeys = {
  pasteTextAfterContinuing: 'pasteTextAfterContinuing',
  continueToPlatform: 'continueToPlatform',
  clickOnInstagramIcon: 'clickOnInstagramIcon',
  pasteCaptionInTextbox: 'pasteCaptionInTextbox',
  openPhoneShareMenu: 'openPhoneShareMenu',
  selectAppToShare: 'selectAppToShare',
  fillInBlank: 'fillInBlank',
  openShareMenu: 'openShareMenu',
  selectPlatformApp: 'selectPlatformApp',
  followSteps: 'followSteps',
  clickButtonBelow: 'clickButtonBelow',
  selectDownloadedFile: 'selectDownloadedFile',
  textCopied: 'textCopied',
  termsOfUse: 'termsOfUse',
  createdBy: 'createdBy',
  readMore: 'readMore',
  showLess: 'showLess',
  optimizeAndTweet: 'optimizeAndTweet',
  loadingPosts: 'loadingPosts'
};

const i18nFrench = {
  [i18nKeys.pasteTextAfterContinuing]: "Veuillez coller le texte après avoir continué",
  [i18nKeys.continueToPlatform]: "Ouvrir []",
  [i18nKeys.clickOnInstagramIcon]: "Cliquez sur [] dans le menu d'en-tête",
  [i18nKeys.pasteCaptionInTextbox]: "Coller la légende dans la zone de texte",
  [i18nKeys.openPhoneShareMenu]: "Cliquez sur le bouton ci-dessous pour ouvrir le menu de partage de votre téléphone",
  [i18nKeys.selectAppToShare]: "Sélectionnez l’application avec laquelle vous souhaitez partager cette publication",
  [i18nKeys.fillInBlank]: "Veuillez remplir l’espace vide avant de partager",
  [i18nKeys.selectPlatformApp]: "Sélectionnez l’application []",
  [i18nKeys.openShareMenu]: "Ouvrir le menu",
  [i18nKeys.followSteps]: "Suivez ces étapes pour créer une publication",
  [i18nKeys.clickButtonBelow]: "Cliquer sur le bouton ci-dessous pour ouvrir []",
  [i18nKeys.selectDownloadedFile]: "Sélectionner le fichier téléchargé",
  [i18nKeys.textCopied]: "Texte copié",
  [i18nKeys.termsOfUse]: "Conditions générales d’utilisation",
  [i18nKeys.createdBy]: "",
  [i18nKeys.readMore]: "en savoir plus",
  [i18nKeys.showLess]: "afficher moins",
  [i18nKeys.optimizeAndTweet]: "Optimisez et microbloguez",
  [i18nKeys.loadingPosts]: "Chargement des publications"
};

const i18nEnglish = {
  [i18nKeys.pasteTextAfterContinuing]: "Please paste the text after continuing.",
  [i18nKeys.continueToPlatform]: "Continue to []",
  [i18nKeys.clickOnInstagramIcon]: "Click on [] Create in the sidebar menu",
  [i18nKeys.pasteCaptionInTextbox]: "Paste the caption in the textbox",
  [i18nKeys.openPhoneShareMenu]: "Click the button below to open your phone's share menu",
  [i18nKeys.selectAppToShare]: "Select the app you want to share this post with",
  [i18nKeys.fillInBlank]: "Please fill in the blank before sharing",
  [i18nKeys.selectPlatformApp]: "Select the [] app",
  [i18nKeys.openShareMenu]: "Open share menu",
  [i18nKeys.followSteps]: "Follow these steps to create a post",
  [i18nKeys.clickButtonBelow]: "Click on the button below to open []",
  [i18nKeys.selectDownloadedFile]: "Select your downloaded file",
  [i18nKeys.textCopied]: "Text copied",
  [i18nKeys.termsOfUse]: "Terms of Use",
  [i18nKeys.createdBy]: "Created By",
  [i18nKeys.readMore]: "read more",
  [i18nKeys.showLess]: "show less",
  [i18nKeys.optimizeAndTweet]: "Optimize and Tweet",
  [i18nKeys.loadingPosts]: "Loading Posts",
};

const i18nCatalan = {
  [i18nKeys.pasteTextAfterContinuing]: "Si us plau, enganxa el text després de continuar.",
  [i18nKeys.continueToPlatform]: "Continua a []",
  [i18nKeys.clickOnInstagramIcon]: "Fes clic a [] al menú de la barra lateral",
  [i18nKeys.pasteCaptionInTextbox]: "Enganxa el títol al quadre de text",
  [i18nKeys.openPhoneShareMenu]: "Fes clic al botó següent per obrir al teu telèfon el menú per compartir",
  [i18nKeys.selectAppToShare]: "Selecciona l'aplicació amb la qual vols compartir aquesta publicació",
  [i18nKeys.fillInBlank]: "Si us plau, omple el buit abans de compartir",
  [i18nKeys.selectPlatformApp]: "Selecciona l'aplicació [].",
  [i18nKeys.openShareMenu]: "Obre el menú per compartir",
  [i18nKeys.followSteps]: "Segueix aquests passos per crear una publicació",
  [i18nKeys.clickButtonBelow]: "Fes clic al botó de sota per obrir",
  [i18nKeys.selectDownloadedFile]: "Selecciona el fitxer descarregat",
  [i18nKeys.textCopied]: "S'ha copiat el text al porta-retalls"
};

const i18nCatalanV2 = {
  [i18nKeys.pasteTextAfterContinuing]: "Si us plau, enganxa el text després.",
  [i18nKeys.continueToPlatform]: "Continua a []",
  [i18nKeys.clickOnInstagramIcon]: "Fes clic a [] al menú de la barra lateral",
  [i18nKeys.pasteCaptionInTextbox]: "Enganxa el títol al quadre de text",
  [i18nKeys.openPhoneShareMenu]: "Fes clic al botó per accedir al menú de compartició",
  [i18nKeys.selectAppToShare]: "Selecciona l'aplicació on vols compartir-ho",
  [i18nKeys.fillInBlank]: "Si us plau, omple el buit abans de compartir",
  [i18nKeys.selectPlatformApp]: "Selecciona [].",
  [i18nKeys.openShareMenu]: "Obre el menú per compartir",
  [i18nKeys.followSteps]: "Segueix aquests passos per crear una publicació",
  [i18nKeys.clickButtonBelow]: "Fes clic al botó de sota per obrir",
  [i18nKeys.selectDownloadedFile]: "Selecciona el fitxer descarregat",
  [i18nKeys.textCopied]: "S'ha copiat el text al porta-retalls"
};

const i18nItalian = {
  [i18nKeys.pasteTextAfterContinuing]: "Incolla il testo dopo aver continuato.",
  [i18nKeys.continueToPlatform]: "Continua su []",
  [i18nKeys.clickOnInstagramIcon]: "Clicca su [] nel menù a lato",
  [i18nKeys.pasteCaptionInTextbox]: "Incolla la caption nel box di testo",
  [i18nKeys.openPhoneShareMenu]: "Clicca sul pulsante qui sotto per aprire il menu di condivisione del tuo telefono",
  [i18nKeys.selectAppToShare]: "Seleziona l'app con cui vuoi condividere questo post",
  [i18nKeys.fillInBlank]: "Completa il campo prima di continuare",
  [i18nKeys.selectPlatformApp]: "Seleziona l’app []",
  [i18nKeys.openShareMenu]: "Apri il menù di condivisione",
  [i18nKeys.followSteps]: "Segui questi passaggi per creare un post",
  [i18nKeys.clickButtonBelow]: "Clicca sul pulsante qui sotto per aprire",
  [i18nKeys.selectDownloadedFile]: "Seleziona il file scaricato",
  [i18nKeys.textCopied]: "Testo copiato negli appunti"
};

const i18nSpanish = {
  [i18nKeys.pasteTextAfterContinuing]: "Pega el texto después de continuar.",
  [i18nKeys.continueToPlatform]: "Continúa en []",
  [i18nKeys.clickOnInstagramIcon]: "Pulsa en el icono [] del menú de la barra lateral",
  [i18nKeys.pasteCaptionInTextbox]: "Pega el texto en la caja de texto",
  [i18nKeys.openPhoneShareMenu]: "Pulsa en el siguiente botón para abrir el menú de compartir en tu teléfono",
  [i18nKeys.selectAppToShare]: "Selecciona la aplicación con la que quieres compartir este mensaje",
  [i18nKeys.fillInBlank]: "Rellena el espacio antes de compartir",
  [i18nKeys.selectPlatformApp]: "Selecciona la aplicación []",
  [i18nKeys.openShareMenu]: "Abre el menú de compartir",
  [i18nKeys.followSteps]: "Sigue estos pasos para crear un mensaje",
  [i18nKeys.clickButtonBelow]: "Pulsa en el siguiente botón para abrirlo []",
  [i18nKeys.selectDownloadedFile]: "Selecciona tu archivo descargado",
  [i18nKeys.textCopied]: "Texto copiado"
};

const i18nHungarian = {
  [i18nKeys.pasteTextAfterContinuing]: "A folytatást követően illeszd be a szöveget",
  [i18nKeys.continueToPlatform]: "Folytatás: []",
  [i18nKeys.clickOnInstagramIcon]: "Kattints a [] jelre az applikációban",
  [i18nKeys.pasteCaptionInTextbox]: "Illeszd be a szöveget a megfelelő helyre",
  [i18nKeys.openPhoneShareMenu]: "Kattints a gombra a megosztási menü megnyitásához",
  [i18nKeys.selectAppToShare]: "Válaszd ki, melyik alkalmazásban szeretnéd megosztani a posztot",
  [i18nKeys.fillInBlank]: "Töltsd ki az üresen hagyott részeket megosztás előtt",
  [i18nKeys.selectPlatformApp]: "Válaszd ki a(z) [] alkalmazást",
  [i18nKeys.openShareMenu]: "Megosztás",
  [i18nKeys.followSteps]: "Kövesd az alábbi lépéseket a poszt létrehozásához",
  [i18nKeys.clickButtonBelow]: "Kattints a gombra a megnyitáshoz: []",
  [i18nKeys.selectDownloadedFile]: "Válaszd ki a letöltött fájlt",
  [i18nKeys.textCopied]: "Szöveg kimásolva"
};

const i18nGerman = {
  [i18nKeys.pasteTextAfterContinuing]: "Klicke auf die Schaltfläche und füge dann den Text aus der Zwischenablage ein",
  [i18nKeys.continueToPlatform]: "Weiter zu []",
  [i18nKeys.clickOnInstagramIcon]: "Klicken Sie auf []",
  [i18nKeys.pasteCaptionInTextbox]: "Füge den Text aus der Zwischenablage ein",
  [i18nKeys.openPhoneShareMenu]: "Klicken Sie auf die Schaltfläche \"Weiter zu []\"",
  [i18nKeys.selectAppToShare]: "Wählen Sie die gewünschte App",
  [i18nKeys.fillInBlank]: "Bitte befüllen Sie das freie Feld",
  [i18nKeys.selectPlatformApp]: "Wählen Sie die [] App",
  [i18nKeys.openShareMenu]: "Öffne Menü",
  [i18nKeys.followSteps]: "Bitte folge diesen Schritten, um deinen Post zu teilen",
  [i18nKeys.clickButtonBelow]: "Klicke auf die Schaltfläche um [] zu öffnen",
  [i18nKeys.selectDownloadedFile]: "Wähle die heruntergeladene Datei",
  [i18nKeys.textCopied]: "Text kopiert"
};

const i18nPolish = {
  [i18nKeys.pasteTextAfterContinuing]: "W następnym kroku wklej tekst.",
  [i18nKeys.continueToPlatform]: "Przejdź do []",
  [i18nKeys.clickOnInstagramIcon]: "Kliknij ikonę [] w menu paska bocznego.",
  [i18nKeys.pasteCaptionInTextbox]: "Wklej opis w polu tekstowym",
  [i18nKeys.openPhoneShareMenu]: "Kliknij poniższy przycisk, aby otworzyć opcje udostępniania w telefonie.",
  [i18nKeys.selectAppToShare]: "Wybierz aplikację, na której chcesz udostępnić ten post.",
  [i18nKeys.fillInBlank]: "Wypełnij puste pole przed udostępnieniem",
  [i18nKeys.selectPlatformApp]: "Wybierz aplikację []",
  [i18nKeys.openShareMenu]: "Otwórz okno udostępniania",
  [i18nKeys.followSteps]: "Postępuj zgodnie z tymi krokami, aby stworzyć post",
  [i18nKeys.clickButtonBelow]: "Kliknij poniższy przycisk, aby otworzyć []",
  [i18nKeys.selectDownloadedFile]: "Wybierz pobrany plik",
  [i18nKeys.textCopied]: "Tekst skopiowany"
};

const i18nRomanian = {
  [i18nKeys.fillInBlank]: "Nu uita să completezi informațiile cerute în spațiul încadrat de parantezele drepte",
  [i18nKeys.clickOnInstagramIcon]: "Apasă/click pe [] în meniul din lateral",
  [i18nKeys.openPhoneShareMenu]: "Apasă/click pe butonul de mai jos pentru a deschide meniul de share al telefonului",
  [i18nKeys.selectPlatformApp]: "Selectează aplicația []",
  [i18nKeys.selectAppToShare]: "Selectează aplicația pe care vrei să distribui postarea",
  [i18nKeys.openShareMenu]: "Deschide meniul de share"
};

export class I18n {
  constructor(userID=null) {
    if(userID === '37FBF626-F59A-43D0-AEDB-23F9FD98C968') {
      this.language = "romanian";
    } else if(userID === '1A571E74-5CCE-413E-933F-A9171EE7925B') {
      this.language = "hungarian";
    } else if((userID === '14F69928-CE82-4221-834E-0BD8C015D885') || (window.location.hostname === "act.fermiamolicolvoto.it") || (userID === 'D016C170-48D2-4BC8-86AD-5FDEDCDD2EF1')) {
      this.language = "italian";
    } else if(userID === '1C63CE89-0B11-429A-9D00-D668D99FD2FD') {
      this.language = "catalan";
    } else if((userID === 'E7060A3D-1D16-4524-BC4F-22C7C5480C98') ||  (window.location.hostname === "compartir.comuns.cat")){
      this.language = "catalan_v2";
    } else if((window.location.hostname === "partage.croixrouge.ca") ||  (window.location.hostname === "partagez.travaillerpourlavenir.ca") ||  (window.location.hostname === "partagez.congresdutravail.ca")) {
      this.language = "french";
    } else if((window.location.hostname === "app.social-bridge.at")  ||  (window.location.hostname === "teilen.carolarackete.eu")) {
      this.language = "german";
    } else if((window.location.hostname === "megosztas.juhaszpeter.org") || (window.location.hostname === "link.noar.hu") || (window.location.hostname === "megosztas.patent.org.hu") || (window.location.hostname === "sosha.lundadonate.org")) {
      this.language = "hungarian";
    } else if((window.location.hostname === "comparte.oregonfoodbank.org") || (window.location.hostname === "pasalo.movimientosumar.es")) {
      this.language = "spanish";
    } else if ((window.location.hostname === "link.wschod.org")) {
      this.language = "polish";
    } else {
      this.language = "english";
    }

    this.langDict = 
        this.language === "french" ? i18nFrench 
      : this.language === "german" ? i18nGerman
      : this.language === "hungarian" ? i18nHungarian
      : this.language === "polish"  ? i18nPolish
      : this.language === "spanish" ? i18nSpanish
      : this.language === "romanian" ? i18nRomanian
      : this.language === "italian" ? i18nItalian
      : this.language === "catalan" ? i18nCatalan
      : this.language === "catalan_v2" ? i18nCatalanV2
      : i18nEnglish;
  }

  getText(textKey, param=null) {
    let txt = textKey in this.langDict ? this.langDict[textKey] : i18nEnglish[textKey];

    if(param && txt.includes("[]")) {
      if(typeof param === "string") {
        txt = txt.replace("[]",param);
      } else {
        const parts = txt.split("[]");
        txt = <>{parts[0]}{param}{parts[1]}</>;
      }
    }
  
    return txt;
  }
}

